import { captureException } from "@sentry/browser";
import {
  hasMarketingConsent,
  hasUserResponded,
} from "../utils/cookiebotConsent";
import { getCookie } from "../theme/modules/cookies";

export const CLICK_ID_PARAMS = [
  "gclid",
  "gclsrc",
  "wbraid",
  "gbraid",
  "msclkid",
  "fbclid",
];
const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const COOKIE_PREFIX = "_trk_";
const TTL_CLICK_ID = 7_776_000; // 90 days in seconds
const TTL_UTM = 2_592_000; // 30 days in seconds
const SESSION_KEY = "_attr";
const TTL_TIMESTAMP = TTL_CLICK_ID; // 90 days — aligned with Worker's TTL_TIMESTAMP
const COOKIE_DOMAIN = ".alzproducts.co.uk";

/**
 * Sets a single tracking cookie with the _trk_ prefix and correct attributes.
 * @param {string} name - The param name (without prefix).
 * @param {string} value - The cookie value (will be URI-encoded).
 * @param {number} maxAgeSeconds - Cookie lifetime in seconds.
 * @returns {void}
 */
function setTrackingCookie(name, value, maxAgeSeconds) {
  document.cookie = `${COOKIE_PREFIX}${name}=${encodeURIComponent(value)}; Path=/; Domain=${COOKIE_DOMAIN}; Max-Age=${maxAgeSeconds}; SameSite=Lax; Secure`;
}

/**
 * Deletes a tracking cookie by setting Max-Age=0 with matching Path and Domain.
 * @param {string} name - The param name (without prefix).
 * @returns {void}
 */
function deleteTrackingCookie(name) {
  document.cookie = `${COOKIE_PREFIX}${name}=; Path=/; Domain=${COOKIE_DOMAIN}; Max-Age=0; SameSite=Lax; Secure`;
}

/**
 * Reads tracking params from the current URL and writes them to sessionStorage.
 * Runs unconditionally — no consent required for sessionStorage.
 * @returns {void}
 */
function captureUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const params = {};

  [...CLICK_ID_PARAMS, ...UTM_PARAMS].forEach((param) => {
    const value = searchParams.get(param);
    if (value) {
      params[param] = value;
    }
  });

  if (Object.keys(params).length > 0) {
    const ts = Math.floor(Date.now() / 1000);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ params, ts }));
  }
}

/**
 * Clears existing click ID cookies for platforms not represented in newClickIds.
 * Prevents stale click IDs from a previous ad platform persisting when a new one arrives.
 * @param {string[]} newClickIds - Click ID param names present in the new attribution data.
 * @returns {void}
 */
function clearStaleClickIds(newClickIds) {
  CLICK_ID_PARAMS.forEach((param) => {
    if (!newClickIds.includes(param)) {
      deleteTrackingCookie(param);
    }
  });
}

/**
 * Reads attribution data from sessionStorage and writes _trk_* cookies.
 * Clears sessionStorage after persisting. No-op if sessionStorage is empty.
 * @returns {void}
 */
function persistToCookies() {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) {
    return;
  }

  const { params, ts } = JSON.parse(raw);

  const newClickIds = CLICK_ID_PARAMS.filter((p) => params[p]);
  if (newClickIds.length > 0) {
    clearStaleClickIds(newClickIds);
  }

  CLICK_ID_PARAMS.forEach((param) => {
    if (params[param]) {
      setTrackingCookie(param, params[param], TTL_CLICK_ID);
    }
  });

  UTM_PARAMS.forEach((param) => {
    if (params[param]) {
      setTrackingCookie(param, params[param], TTL_UTM);
    }
  });

  if (ts) {
    setTrackingCookie("ts", String(ts), TTL_TIMESTAMP);
  }

  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Returns true if the Cloudflare Worker has set server-side tracking cookies.
 * Checks for the _trk_srv marker cookie, which the Worker sets after upgrading
 * JS-set cookies to durable server-set first-party cookies.
 * Used by Mixpanel integration to annotate Page Viewed events.
 * @returns {boolean} Whether server-set tracking cookies exist.
 */
export function hasTrackingCookies() {
  return document.cookie.includes(`${COOKIE_PREFIX}srv=`);
}

/**
 * @typedef {object} AttributionData
 * @property {string|null} gclid - Google Click ID.
 * @property {string|null} gclsrc - Google Ads source type identifier.
 * @property {string|null} wbraid - Google web-to-app attribution (iOS 14+).
 * @property {string|null} gbraid - Google app-to-web attribution (iOS 14+).
 * @property {string|null} msclkid - Microsoft/Bing Ads click identifier.
 * @property {string|null} fbclid - Facebook/Meta click identifier.
 * @property {string|null} utm_source - Campaign source.
 * @property {string|null} utm_medium - Campaign medium.
 * @property {string|null} utm_campaign - Campaign name.
 * @property {string|null} utm_content - Ad content identifier.
 * @property {string|null} utm_term - Search keywords.
 */

/**
 * Reads attribution data from consent-gated _trk_* cookies.
 * Only returns data after marketing consent has been granted (cookies only
 * exist post-consent). Returns null if no tracking cookies are present.
 * @returns {AttributionData|null} Attribution data object, or null if no tracking cookies.
 */
export function getAttributionData() {
  const fields = [...CLICK_ID_PARAMS, ...UTM_PARAMS];
  const result = {};

  for (const field of fields) {
    const val = getCookie(`${COOKIE_PREFIX}${field}`);
    result[field] = val !== "" ? val : null;
  }

  const hasAnyValue = Object.values(result).some((v) => v !== null);
  return hasAnyValue ? result : null;
}

/**
 * Core attribution initialisation logic.
 * @returns {void}
 */
function initAttributionCore() {
  captureUrlParams();

  // Mirror mixpanel.js issue #235 pattern: handle returning visitors who already consented
  if (hasUserResponded() && hasMarketingConsent()) {
    persistToCookies();
    return;
  }

  window.addEventListener("CookiebotOnAccept", function () {
    if (hasMarketingConsent()) {
      persistToCookies();
    }
  });
}

/**
 * Initialises client-side attribution capture and cookie persistence.
 * Captures URL tracking params unconditionally, then persists to cookies when
 * Cookiebot marketing consent is granted. Call once on every page load.
 * @returns {void}
 */
export function initAttribution() {
  try {
    initAttributionCore();
  } catch (error) {
    captureException(error, {
      tags: { component: "attribution" },
      level: "error",
    });
  }
}
